import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useDiscoveryClientOptions } from '../api/context';
import { createSearchApi, type SearchApi } from '../api/client';
import { useCapabilities } from '../hooks/capabilities';
import type {
  IncludeSource,
  JobOverlap,
  LeadEnrichment,
  NearMisses,
  SavedSearch,
  ScrapeJobLogEntry,
  ScrapeParams,
  SearchJobPoll,
  SearchSchema,
  SettingValue,
  SkipBreakdown,
  WidenOption,
} from '../api/types';
import { FALLBACK_SCHEMA } from './fallbackSchema';
import { normaliseSchema } from './params';
import { isSettingsResponse } from './settingsModel';

export function useSearchApi(): SearchApi {
  const options = useDiscoveryClientOptions();
  return useMemo(() => createSearchApi(options), [options]);
}

/**
 * Every cached query that mentions this folder — its leads, counts, history.
 * Matched by id rather than by exact key so it keeps working while the folder
 * screens' own query keys change.
 */
export function invalidateFolder(qc: QueryClient, folderPublicId: string): Promise<void> {
  return qc.invalidateQueries({ predicate: (q) => JSON.stringify(q.queryKey).includes(folderPublicId) });
}

// ── Schema ────────────────────────────────────────────────────────────────

export interface SchemaState {
  schema: SearchSchema;
  /** True while running on the built-in form (endpoint missing or failing). */
  isFallback: boolean;
  isLoading: boolean;
}

/**
 * GET /api/search/schema, falling back to FALLBACK_SCHEMA (today's form) when
 * the endpoint is missing or broken, so the form never disappears. The
 * fallback's platforms follow /api/capabilities, as the old form did.
 */
export function useSearchSchema(): SchemaState {
  const api = useSearchApi();
  const capabilities = useCapabilities();
  const q = useQuery({
    queryKey: ['discovery', 'search', 'schema'],
    queryFn: async () => normaliseSchema(await api.schema()),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const fallback = useMemo<SearchSchema>(() => {
    const live = capabilities.platforms?.length ? capabilities.platforms : ['tiktok'];
    return {
      ...FALLBACK_SCHEMA,
      platforms: FALLBACK_SCHEMA.platforms.map((p) => ({ ...p, available: live.includes(p.id) })),
    };
  }, [capabilities.platforms]);
  if (q.data) return { schema: q.data, isFallback: false, isLoading: false };
  return { schema: fallback, isFallback: !q.isLoading, isLoading: q.isLoading };
}

// ── Saved searches ────────────────────────────────────────────────────────

const savedKey = ['discovery', 'search', 'saved'];

export function useSavedSearches() {
  const api = useSearchApi();
  return useQuery<SavedSearch[]>({ queryKey: savedKey, queryFn: () => api.savedSearches.list() });
}

export function useSaveSearch() {
  const api = useSearchApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; params: Record<string, unknown> }) => api.savedSearches.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: savedKey }),
  });
}

export function useUpdateSavedSearch() {
  const api = useSearchApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; patch: { name?: string; params?: Record<string, unknown> } }) =>
      api.savedSearches.update(v.id, v.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: savedKey }),
  });
}

export function useDeleteSavedSearch() {
  const api = useSearchApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.savedSearches.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: savedKey }),
  });
}

// ── Running a search ──────────────────────────────────────────────────────

export function useStartSearch() {
  const api = useSearchApi();
  return useMutation({ mutationFn: (params: ScrapeParams) => api.start(params) });
}

export function useFolderLeadSample(folderPublicId: string) {
  const api = useSearchApi();
  return useQuery({
    queryKey: ['discovery', 'search', 'lead-sample', folderPublicId],
    queryFn: () => api.folderLeadSample(folderPublicId),
    enabled: !!folderPublicId,
    staleTime: 60_000,
  });
}

export interface SearchJobState {
  status: 'idle' | 'running' | 'done' | 'error';
  logs: ScrapeJobLogEntry[];
  leads: LeadEnrichment[];
  saved: number;
  checked: number;
  cacheHits: number;
  apiCalls: number;
  preFiltered: number;
  skipped: number;
  skipBreakdown: SkipBreakdown | null;
  stage: string | null;
  overlap: JobOverlap | null;
  nearMisses: NearMisses | null;
  /** Legacy widen output — only read when `nearMisses` is absent. */
  widen: WidenOption[] | null;
  finishedAt: string | null;
  error: string | null;
}

const EMPTY_JOB: SearchJobState = {
  status: 'idle',
  logs: [],
  leads: [],
  saved: 0,
  checked: 0,
  cacheHits: 0,
  apiCalls: 0,
  preFiltered: 0,
  skipped: 0,
  skipBreakdown: null,
  stage: null,
  overlap: null,
  nearMisses: null,
  widen: null,
  finishedAt: null,
  error: null,
};

/**
 * Polls a search job every 1.2s with `since` cursors, like useScrapeJobPoll,
 * and also keeps the §5 insights (overlap + near misses), which arrive as a
 * whole snapshot on each poll rather than incrementally.
 */
export function useSearchJob(jobId: string | null, folderPublicId: string): SearchJobState {
  const api = useSearchApi();
  const qc = useQueryClient();
  const [state, setState] = useState<SearchJobState>(EMPTY_JOB);
  const logIndex = useRef(0);
  const leadIndex = useRef(0);

  useEffect(() => {
    logIndex.current = 0;
    leadIndex.current = 0;
    setState(jobId ? { ...EMPTY_JOB, status: 'running' } : EMPTY_JOB);
    if (!jobId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function tick() {
      if (cancelled || !jobId) return;
      try {
        const poll: SearchJobPoll = await api.poll(jobId, logIndex.current, leadIndex.current);
        if (cancelled) return;
        const logs = Array.isArray(poll.logs) ? poll.logs : [];
        const leads = Array.isArray(poll.newLeads) ? poll.newLeads : [];
        logIndex.current += logs.length;
        leadIndex.current += leads.length;
        setState((prev) => ({
          ...prev,
          status: poll.status === 'running' ? 'running' : poll.status,
          logs: logs.length ? [...prev.logs, ...logs] : prev.logs,
          leads: leads.length ? [...prev.leads, ...leads] : prev.leads,
          saved: poll.saved ?? prev.saved,
          checked: poll.checked ?? prev.checked,
          cacheHits: poll.cacheHits ?? prev.cacheHits,
          apiCalls: poll.apiCalls ?? prev.apiCalls,
          preFiltered: poll.preFiltered ?? prev.preFiltered,
          skipped: poll.skipped ?? 0,
          skipBreakdown: poll.skipBreakdown ?? null,
          stage: poll.stage ?? null,
          overlap: poll.overlap ?? prev.overlap,
          nearMisses: poll.nearMisses ?? prev.nearMisses,
          widen: poll.widen ?? poll.widenAnalysis ?? prev.widen,
          finishedAt: poll.finishedAt ?? null,
          error: null,
        }));
        if (poll.status === 'done' || poll.status === 'error') {
          void invalidateFolder(qc, folderPublicId);
          return;
        }
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({ ...prev, status: 'error', error: err instanceof Error ? err.message : 'Polling failed' }));
        return;
      }
      timer = setTimeout(tick, 1200);
    }

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, api, folderPublicId, qc]);

  return state;
}

/** A finished job's insights (Activity). */
export function useJobInsights(jobId: string | null) {
  const api = useSearchApi();
  return useQuery({
    queryKey: ['discovery', 'search', 'job', jobId],
    queryFn: () => api.job(jobId!),
    enabled: !!jobId,
  });
}

/**
 * Add stored overlap / near-miss candidates to the job's folder. No new
 * search: the server copies them from what the job already fetched.
 */
export function useIncludeCreators(jobId: string | null, folderPublicId: string) {
  const api = useSearchApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { usernames: string[]; source: IncludeSource }) => {
      if (!jobId) throw new Error('No search to add from');
      return api.include(jobId, v.usernames, v.source);
    },
    onSuccess: () => {
      void invalidateFolder(qc, folderPublicId);
      if (jobId) void qc.invalidateQueries({ queryKey: ['discovery', 'search', 'job', jobId] });
    },
  });
}

// ── Settings ──────────────────────────────────────────────────────────────

const settingsKey = ['discovery', 'settings'];

export function useDiscoverySettings() {
  const api = useSearchApi();
  return useQuery({
    queryKey: settingsKey,
    queryFn: async () => {
      const res = await api.settings.get();
      if (!isSettingsResponse(res)) throw new Error("This discovery server doesn't have settings yet.");
      return res;
    },
    retry: false,
  });
}

export function useSettingsAudit(limit = 50) {
  const api = useSearchApi();
  return useQuery({
    queryKey: [...settingsKey, 'audit', limit],
    queryFn: async () => {
      const rows = await api.settings.audit(limit);
      return Array.isArray(rows) ? rows : [];
    },
    retry: false,
  });
}

function useSettingsMutation<T>(fn: (api: SearchApi, input: T) => ReturnType<SearchApi['settings']['get']>) {
  const api = useSearchApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: T) => fn(api, input),
    onSuccess: (data) => {
      if (isSettingsResponse(data)) qc.setQueryData(settingsKey, data);
      void qc.invalidateQueries({ queryKey: settingsKey });
      // Search defaults come from settings.
      void qc.invalidateQueries({ queryKey: ['discovery', 'search', 'schema'] });
    },
  });
}

export function useSaveSettings() {
  return useSettingsMutation<Record<string, SettingValue>>((api, values) => api.settings.update(values));
}

export function useResetSettings() {
  return useSettingsMutation<string[]>((api, keys) => api.settings.reset(keys));
}
